import { api } from '../../services/api';

export type AssetType = 'image' | 'video' | 'font' | 'vector';

export interface Asset {
    _id: string;
    name: string;
    url: string;
    type: AssetType;
    thumbnail?: string;
    size?: number;
    mimeType?: string;
    createdAt: string;
}

export interface GetAssetsRequest {
    type?: AssetType;
    page?: number;
    limit?: number;
    search?: string;
}

export interface GetAssetsResponse {
    success: boolean;
    data: Asset[];
    meta: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
}

export const assetsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getAssets: builder.query<GetAssetsResponse, GetAssetsRequest | void>({
            query: (params) => ({
                url: '/editor/assets',
                method: 'GET',
                params: params || {},
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.data.map(({ _id }) => ({ type: 'Assets' as const, id: _id })),
                        { type: 'Assets', id: 'LIST' },
                    ]
                    : [{ type: 'Assets', id: 'LIST' }],
        }),
        uploadAsset: builder.mutation<Asset | { data: Asset; success?: boolean }, FormData>({
            query: (formData) => ({
                url: '/editor/assets',
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: [{ type: 'Assets', id: 'LIST' }],
        }),
        deleteAsset: builder.mutation<{ success: boolean; id: string }, string>({
            query: (id) => ({
                url: `/editor/assets/${id}`,
                method: 'DELETE',
            }),
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    assetsApi.util.updateQueryData('getAssets', undefined, (draft) => {
                        if (draft && draft.data) {
                            draft.data = draft.data.filter((asset) => asset._id !== id);
                            if (draft.meta) {
                                draft.meta.total = Math.max(0, draft.meta.total - 1);
                            }
                        }
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: (_result, _error, id) => [{ type: 'Assets', id }],
        }),
    }),
});

export const { useGetAssetsQuery, useUploadAssetMutation, useDeleteAssetMutation } = assetsApi;
